import { useState, useMemo, useCallback, useRef } from "react";
import ReactFlow, { Background, Controls, MiniMap, type Node, type Edge, MarkerType } from "reactflow";
import "reactflow/dist/style.css";
import { apiGet } from "../../lib/api";
import { layoutGraph } from "./layout";
import AddressNode from "./AddressNode";
import type { AddressNodeData } from "./AddressNode";
import FilterPanel, { DEFAULT_FILTERS, type GraphFilters } from "./FilterPanel";
import { exportGraphJson, exportGraphPng } from "./export";
import type { RawGraph } from "./types";
import { formatAmount, type Network } from "../../lib/units";

const nodeTypes = { address: AddressNode };

const PATTERN_COLOR: Record<string, string> = {
  "simple-transfer": "#4A90C2",
  "possible-consolidation": "#C98A2E",
  "possible-splitting": "#C98A2E",
  "possible-change": "#8B96A5",
};

function mergeGraphs(base: RawGraph, addition: RawGraph): RawGraph {
  const nodeMap = new Map(base.nodes.map((n) => [n.address, n]));
  for (const n of addition.nodes) nodeMap.set(n.address, n);

  const edgeKey = (e: RawGraph["edges"][number]) => `${e.from}->${e.to}->${e.txid}`;
  const edgeMap = new Map(base.edges.map((e) => [edgeKey(e), e]));
  for (const e of addition.edges) edgeMap.set(edgeKey(e), e);

  return {
    nodes: [...nodeMap.values()],
    edges: [...edgeMap.values()],
    truncated: base.truncated || addition.truncated,
  };
}

export default function GraphView({
  initialGraph,
  rootAddress,
  network,
  disableExpand = false,
}: {
  initialGraph: RawGraph;
  rootAddress: string;
  network: Network;
  disableExpand?: boolean;
}) {
  const [graph, setGraph] = useState(initialGraph);
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [expanding, setExpanding] = useState<Set<string>>(new Set());
  const [filters, setFilters] = useState<GraphFilters>(DEFAULT_FILTERS);
  const canvasRef = useRef<HTMLDivElement>(null);

  const toggleCollapse = useCallback((address: string) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(address)) next.delete(address);
      else next.add(address);
      return next;
    });
  }, []);

  const expandNode = useCallback(async (address: string) => {
    setExpanding((prev) => new Set(prev).add(address));
    try {
      const more = await apiGet<RawGraph>(`/api/trace/${network}/graph?address=${address}&maxHops=1`);
      setGraph((prev) => mergeGraphs(prev, more));
    } catch {
      // Leave the graph as-is on failure — the expand button stays clickable to retry.
    } finally {
      setExpanding((prev) => {
        const next = new Set(prev);
        next.delete(address);
        return next;
      });
    }
  }, [network]);

  const maxValueSats = useMemo(
    () => Math.max(1, ...graph.edges.map((e) => e.valueSats)),
    [graph.edges]
  );

  // Apply amount/date filters to edges, then drop nodes that become unreachable from root.
  const filteredGraph = useMemo<RawGraph>(() => {
    const fromTs = filters.fromDate ? new Date(filters.fromDate).getTime() / 1000 : -Infinity;
    const toTs = filters.toDate ? new Date(filters.toDate).getTime() / 1000 + 86400 : Infinity;

    const edges = graph.edges.filter((e) => {
      if (e.valueSats < filters.minValueSats) return false;
      if (e.blockTime !== undefined && (e.blockTime < fromTs || e.blockTime > toTs)) return false;
      return true;
    });

    const reachable = new Set([rootAddress]);
    const adjacency = new Map<string, string[]>();
    for (const e of edges) {
      if (!adjacency.has(e.from)) adjacency.set(e.from, []);
      adjacency.get(e.from)!.push(e.to);
    }
    const queue = [rootAddress];
    while (queue.length) {
      const cur = queue.shift()!;
      if (collapsed.has(cur)) continue; // don't traverse past a collapsed node
      for (const next of adjacency.get(cur) ?? []) {
        if (!reachable.has(next)) {
          reachable.add(next);
          queue.push(next);
        }
      }
    }

    return {
      nodes: graph.nodes.filter((n) => reachable.has(n.address)),
      edges: edges.filter((e) => reachable.has(e.from) && reachable.has(e.to) && !collapsed.has(e.from)),
      truncated: graph.truncated,
    };
  }, [graph, filters, collapsed, rootAddress]);

  const hiddenDescendantCounts = useMemo(() => {
    // For each collapsed node, count how many nodes it's hiding downstream (unfiltered graph, so
    // the count stays meaningful even under an active filter).
    const adjacency = new Map<string, string[]>();
    for (const e of graph.edges) {
      if (!adjacency.has(e.from)) adjacency.set(e.from, []);
      adjacency.get(e.from)!.push(e.to);
    }
    const counts = new Map<string, number>();
    for (const address of collapsed) {
      const seen = new Set<string>();
      const queue = [...(adjacency.get(address) ?? [])];
      while (queue.length) {
        const cur = queue.shift()!;
        if (seen.has(cur)) continue;
        seen.add(cur);
        queue.push(...(adjacency.get(cur) ?? []));
      }
      counts.set(address, seen.size);
    }
    return counts;
  }, [graph, collapsed]);

  const childrenOf = useMemo(() => {
    const set = new Set<string>();
    for (const e of graph.edges) set.add(e.from);
    return set;
  }, [graph]);

  const positions = useMemo(() => layoutGraph(filteredGraph, rootAddress), [filteredGraph, rootAddress]);

  const nodes: Node<AddressNodeData>[] = filteredGraph.nodes.map((n) => ({
    id: n.address,
    type: "address",
    position: positions.get(n.address) ?? { x: 0, y: 0 },
    data: {
      address: n.address,
      isRoot: n.address === rootAddress,
      attribution: n.attribution,
      collapsed: collapsed.has(n.address),
      hiddenDescendantCount: hiddenDescendantCounts.get(n.address) ?? 0,
      hasChildren: childrenOf.has(n.address),
      expandable: !disableExpand,
      onToggleCollapse: toggleCollapse,
      onExpand: expandNode,
      expanding: expanding.has(n.address),
    },
  }));

  const edges: Edge[] = filteredGraph.edges.map((e) => ({
    id: `${e.from}-${e.to}-${e.txid}`,
    source: e.from,
    target: e.to,
    label: formatAmount(e.valueSats, network),
    style: { stroke: PATTERN_COLOR[e.pattern] },
    markerEnd: { type: MarkerType.ArrowClosed, color: PATTERN_COLOR[e.pattern] },
    labelStyle: { fill: "#8B96A5", fontSize: 10 },
  }));

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <FilterPanel filters={filters} onChange={setFilters} maxValueSats={maxValueSats} network={network} />
        <div className="flex gap-2 text-xs">
          <button
            onClick={() => exportGraphJson(graph, rootAddress)}
            className="px-3 py-1.5 border border-ink-700 rounded-md text-muted hover:text-paper hover:border-ledger-500 transition-colors"
          >
            Export JSON
          </button>
          <button
            onClick={() => canvasRef.current && exportGraphPng(canvasRef.current, rootAddress)}
            className="px-3 py-1.5 border border-ink-700 rounded-md text-muted hover:text-paper hover:border-ledger-500 transition-colors"
          >
            Export PNG
          </button>
        </div>
      </div>

      {graph.truncated && (
        <p className="text-xs text-muted mb-3">
          This view is bounded (hop/branch limits) — there may be more transactions than shown.
          {disableExpand ? "" : ' Use "Expand further" on any address to look deeper from that point.'}
        </p>
      )}

      <div ref={canvasRef} style={{ height: 480 }} className="border border-ink-700 rounded-md overflow-hidden">
        <ReactFlow nodes={nodes} edges={edges} nodeTypes={nodeTypes} fitView minZoom={0.2} maxZoom={2}>
          <Background color="#26313F" gap={24} />
          <Controls />
          <MiniMap
            nodeColor={() => "#26313F"}
            maskColor="rgba(13,17,23,0.7)"
            style={{ backgroundColor: "#111820" }}
          />
        </ReactFlow>
      </div>
    </div>
  );
}
